/**
 * @file This is the main project file where all other scripts should be initiated.
 *
 * Note that since this file will be appended to the `body` element there is no need
 * to wait for the DOMContentLoaded event before initiating your scripts. In fact, it
 * will not work since the DOMContentLoaded event will have been triggered before this
 * file is loaded. Instead use an iife (1) or, as in the example below, a function
 * that is called when a custom event is triggered.
 *
 * 1. https://en.wikipedia.org/wiki/Immediately-invoked_function_expression
 *
 * Examples
 *
 * Load a self-initiating script that does not expect a DOMContentLoaded event:
 * import './path/to/script.js'
 *
 * Load a script that you need to send parameters to or initiate at a specific time:
 * First import the script:
 * import functionName from './path/to/script.js'
 * Note that the path needs to start with './' and be relative to your code root, which
 * is 'project' by default.
 * Then initiate it:
 * functionName(param);
 *
 * See the included examples for more.
 */

import Vue from "vue";
import VuexStore from "./js/vuex-store/vuex-store.js";
import VueCookies from "./js/utilities/vue-mixins/vue-cookies.js";
import vueTouchEvents from "./js/utilities/vue-mixins/vue-touch-events.js";
import VStaticMap from "@/components/map/static-map/VStaticMap.vue";
import VIcon from "@/components/icon/VIcon.vue";
import VModal from "@/components/modal/VModal";
import VContentBlock from "@/components/content-block/VContentBlock.vue";
import VList from "@/components/list/VList.vue";
import VListItem from "@/components/list-item/VListItem.vue";
import VContactEntity from "@/components/contact/contact-entity/VContactEntity.vue";
import VAppartmentApplication from "@/components/appartment-application/VAppartmentApplication.vue";
import VGallery from "@/components/gallery/VGallery.vue";
import VGalleryImageScroller from "@/components/gallery-image-scroller/VGalleryImageScroller.vue";
import VGalleryImageScrollerSmall from "@/components/gallery-image-scroller-small/VGalleryImageScrollerSmall.vue";
import VTeaserGallery from "@/components/teaser-gallery/VTeaserGallery.vue";
import VTeaserGalleryIntro from "@/components/teaser-gallery-intro/VTeaserGalleryIntro.vue";
import VSearch from "@/components/search/VSearch.vue";
import VSearchDataProvider from "@/components/search/VSearchDataProvider.vue";
import VSearchWrapper from "@/components/search/VSearchWrapper.vue";
import VExpand from "@/components/expand/VExpand.vue";
import VAccordion from "@/components/accordion/VAccordion.vue";
import VSelect from "@/components/form-elements/select/VSelect.vue";
import VSelectDevelopment from "@/components/form-elements/select-development/VSelectDevelopment.vue";
import VMultiSelect from "@/components/form-elements/multi-select/VMultiSelect.vue";
import VMediaGallery from "@/components/media-gallery/VMediaGallery.vue";
import VMediaGalleryModal from "@/components/media-gallery/VMediaGalleryModal.vue";
import VMultiSelectDevelopment from "@/components/form-elements/multi-select-development/VMultiSelectDevelopment.vue";
import VRangeSlider from "@/components/form-elements/range-slider/VRangeSlider.vue";
import VTabs from "@/components/tabs/VTabs.vue";
import VObject from "@/components/object-listing/object/VObject.vue";
import VObjectDevelopment from "@/components/object-listing/object-development/VObjectDevelopment.vue";
import VObjectListingDevelopment from "@/components/object-listing/object-listing-development/VObjectListingDevelopment.vue";
import VObjectListing from "@/components/object-listing/object-listing/VObjectListing.vue";
import VObjectListingDataProvider from "@/components/object-listing/object-listing-data-provider/VObjectListingDataProvider.vue";
import VMessage from "@/components/message/VMessage.vue";
import VArticle from "@/components/article/VArticle.vue";
import VImageModal from "@/components/image-modal/VImageModal.vue";
import VImageHeader from "@/components/image-header/VImageHeader.vue";
import VMultiHero from "@/components/multi-hero/VMultiHero.vue";
import VPageHeader from "@/components/page-header/VPageHeader.vue";
import VMobileMenu from "@/components/mobile-menu/VMobileMenu.vue";
import VMobileMenuItem from "@/components/mobile-menu/VMobileMenuItem.vue";
import VSearchPane from "@/components/search-pane/VSearchPane.vue";
import VLogo from "@/components/logo/VLogo.vue";
import VMainNav from "@/components/navigation/main-nav/VMainNav.vue";
import VQuickLinksBlock from "@/components/quick-links-block/VQuickLinksBlock.vue";
import VQuickLinksBlockItem from "@/components/quick-links-block/VQuickLinksBlockItem.vue";
import VAvailableNowBlock from "@/components/available-now-block/VAvailableNowBlock.vue";
import VAvailableNowBlockItem from "@/components/available-now-block/VAvailableNowBlockItem.vue";
import VInfoBlock from "@/components/info-block/VInfoBlock.vue";
import VInfoVideoBlock from "@/components/info-video-block/VInfoVideoBlock.vue";
import VToc from "@/components/toc/VToc.vue";
import VTocLink from "@/components/toc/VTocLink.vue";
import VContentTocWrapper from "@/components/toc/VContentTocWrapper.vue";
import VPromoBlock from "@/components/promo-block/VPromoBlock.vue";
import VMainNavFaq from "@/components/navigation/main-nav/VMainNavFaq.vue";
import VMainNavContact from "@/components/navigation/main-nav/VMainNavContact.vue";
import VMap from "@/components/map/map/VMap.vue";
import VFiltersWrapper from "@/components/filters-wrapper/VFiltersWrapper.vue";
import VideoPlayer from "@/components/video-player/VideoPlayer.vue";
import VPuffBlock from "@/components/puff-block/VPuffBlock.vue";
import VContact from "@/components/contact/contact/VContact.vue";
import VChecklistFilter from "@/components/checklist-filter/VChecklistFilter.vue";
import VChecklistFilterRadio from "@/components/checklist-filter/VChecklistFilterRadio.vue";
import VChecklistBlock from "@/components/checklist-block/VChecklistBlock.vue";
import VChecklistBlockItem from "@/components/checklist-block/VChecklistBlockItem.vue";
import VChecklistCounter from "@/components/checklist-block/VChecklistCounter.vue";
import VChecklistMediaBlock from "@/components/checklist-media-block/VChecklistMediaBlock.vue";
import VChecklistPopupModal from "@/components/checklist-media-block/VChecklistPopupModal.vue";
import VFaqAccordion from '@/components/faq/VFaqAccordion.vue';
import VFaqAccordionItem from '@/components/faq/VFaqAccordionItem.vue';
import VFaqMenu from '@/components/faq/VFaqMenu.vue';
import VGoogleMapsStatic from "@/components/google-maps/static/VGoogleMapsStatic.vue";
import VQuoteBlock from "@/components/quote-block/VQuoteBlock.vue"
import VDistrictOfficeBlock from "@/components/district-office-block/VDistrictOfficeBlock.vue"
import VFullWidthInfoBlock from '@/components/info-block/VFullWidthInfoBlock.vue';
import VSelectDropdown from '@/components/select-dropdown/VSelectDropdown.vue';

import "./js/vendor/polyfills/polyfill-custom-event.js";
import "./js/vendor/polyfills/polyfill-remove.js";
import "./js/callback.js";
import { MediaQueries } from "vue-media-queries";

const mediaQueries = new MediaQueries();
Vue.use(mediaQueries);
Vue.use(VueCookies);
Vue.use(vueTouchEvents);

new Vue({
	el: "#vue-app",
	components: {
		VQuoteBlock,
		VDistrictOfficeBlock,
		VIcon,
		VGallery,
		VFullWidthInfoBlock,
		VGalleryImageScroller,
		VGalleryImageScrollerSmall,
		VSearch,
		VSearchDataProvider,
		VSearchWrapper,
		VGoogleMapsStatic,
		VTabs,
		VExpand,
		VTeaserGalleryIntro,
		VSelect,
		VSelectDevelopment,
		VMediaGallery,
		VMediaGalleryModal,
		VMultiSelectDevelopment,
		VMultiSelect,
		VRangeSlider,
		VObjectListingDevelopment,
		VObjectListingDataProvider,
		VObjectListing,
		VObject,
		VObjectDevelopment,
		VTeaserGallery,
		VMessage,
		VArticle,
		VImageModal,
		VAccordion,
		VMultiHero,
		VPageHeader,
		VMobileMenu,
		VMobileMenuItem,
		VSearchPane,
		VMainNav,
		VMainNavFaq,
		VMainNavContact,
		VMap,
		VFiltersWrapper,
		VList,
		VListItem,
		VContentBlock,
		VStaticMap,
		VContactEntity,
		VAppartmentApplication,
		VModal,
		VLogo,
		VQuickLinksBlock,
		VQuickLinksBlockItem,
		VInfoBlock,
		VInfoVideoBlock,
		VPromoBlock,
		VAvailableNowBlock,
		VAvailableNowBlockItem,
		VImageHeader,
		VToc,
		VTocLink,
		VContentTocWrapper,
		VideoPlayer,
		VPuffBlock,
		VContact,
		VChecklistFilter,
		VChecklistFilterRadio,
		VChecklistBlock,
		VChecklistBlockItem,
		VChecklistCounter,
		VChecklistMediaBlock,
		VChecklistPopupModal,
		VFaqAccordion,
		VFaqAccordionItem,
		VFaqMenu,
		VSelectDropdown
	},
	store: VuexStore,
	mediaQueries: mediaQueries
});

document.dispatchEvent(new CustomEvent("newcontent"));
